import { Component } from '@angular/core';
import { BasePageCRUDComponent } from '@m4u-angular/base/component/BasePageCRUDComponent';
import { ServiceBagService, ServiceBagServices } from '@m4u-angular/base/component/ServiceBag';
import { 
    BaseEntityResponse,
    BaseState, CredentialExternalInfo, NotificationDispatch, NotificationTemplate, SendNotificationDispatchParams
} from '@m4u-angular/base/entities/BaseEntities';
import { 
    EnumNotificationDeliveryOptions, EnumNotificationStatuses, EnumPageDisplayModes, EnumSystemOptions
} from '@m4u-angular/core/framework/sysenums.enum';
import { NotificationDispatchEndpoint } from '@m4u-angular/core/endpoints/notification-dispatch.endpoint';
import { NotificationTemplateEndpoint } from '@m4u-angular/core/endpoints/notification-template.endpoint';
import { DataSourceCustomAction } from '@m4u-angular/base/datalayer/BaseDataSources';
import { DataLoadOptions } from '@m4u-angular/base/datalayer/DataLoadOptions';
import { DomSanitizer } from '@angular/platform-browser';

export class State extends BaseState {
    Recipients: CredentialExternalInfo[] = [];
    NotificationTemplate: NotificationTemplate;
    NotificationDeliveryOptions: any[] = [];
    Subject: string;
    Body: string;
    Header: string;
    ///When set to true, user should edit only Header text and Body text omiting HTML
    IsSimplifiedHtmlBodyEdit: boolean = false;
}

@Component({
    providers: ServiceBagServices,
    selector: 'app-notification-dispatch',
    templateUrl: './notification-dispatch.component.html',
    styleUrls: ['./notification-dispatch.component.scss']
})
export class NotificationDispatchComponent extends BasePageCRUDComponent<NotificationDispatch, State, NotificationDispatchEndpoint> {

    get IsReadOnly() : boolean {
        return !(this.Entity?.Status == EnumNotificationStatuses.Draft);
    }

    constructor(
        A: ServiceBagService,
        private notificationTemplateEndpoint: NotificationTemplateEndpoint,
        public sanitizer: DomSanitizer,
        private endpoint: NotificationDispatchEndpoint) {
        super(A, 'NotificationDispatchComponent', NotificationDispatch, State, endpoint, EnumPageDisplayModes.Window);
    }

    protected override OnInitComponent() {
        this.LoadNotificationTemplates();
        this.InitDeliveryOptions();
    }

    protected override OnEntityNew() {
        this.Entity.Status = EnumNotificationStatuses.Draft;
        this.Entity.CreatedDate = new Date();
    }

    
    OnNotificationTemplateChange(e) {
        if(e.value) {
            this.DS('NotificationTemplates').FindAsync(e.value).subscribe((item : NotificationTemplate) => {
                this.State.NotificationTemplate = item;

                this.State.IsSimplifiedHtmlBodyEdit = item.Name.startsWith('GenericNotificationDispatch') 
                    && this.Entity.DeliveryMethod == EnumNotificationDeliveryOptions.Email;

                this.State.Body = this.NotificationBody();
                this.State.Subject = item.Subject;
            });
        } else {
            this.State.NotificationTemplate = undefined;
        }
    }

    OnDeliveryMethodChange(e: any) {
        this.State.Body = this.NotificationBody();

        if(e.value != EnumNotificationDeliveryOptions.Email) {
            this.State.Subject = undefined;
        } else if(!this.State.Subject) {
            this.State.Subject = this.State.NotificationTemplate?.Subject;
        }
    }

    NotificationTemplateDisplayExpr(item?: NotificationTemplate) {
        if (!item) return;

        return `${item.Name}_${item.Language}`;
    }

    OnRecipientsSelectionChanged(recipients: CredentialExternalInfo[]) {
        this.State.Recipients = recipients;
    }

    OnSaveNotificationDispatch() {
        if (!this.IsValid()) {
            return;
        }

        this.ValidateValidationGroup(this.validationGroup).then((isValid) => {
            if (isValid) {
                const params = new SendNotificationDispatchParams();
                params.DeliveryMethod = this.Entity.DeliveryMethod;
                params.NotificationTemplateID = this.Entity.NotificationTemplateID;
                params.Name = this.Entity.Name;
                params.Description = this.Entity.Description;
                params.Subject = this.State.Subject;
                params.Body = this.State.Body;
                params.Header = this.State.Header;
                params.IsSimplifiedHtmlBodyEdit = this.State.IsSimplifiedHtmlBodyEdit;

                params.RecipientIDs = this.State.Recipients.map(r => r.EntryID);

                this.Do(
                    this.endpoint.SendNotificationDispatch(params), 
                    false, 
                    (response) => {
                        this.UI.InfoMessage(response.ResponseMessage);
                        this.NotifyEntitySaved(response.Entity, NotificationDispatch);
                        this.ClosePage();
                    },
                    (response) => {
                        this.UI.StopMessage(response.ResponseMessage);                        

                        if(response.ResponseExtendedMessage != 'validationError') {
                            this.NotifyEntitySaved(response.Entity, NotificationDispatch);
                            this.ClosePage();
                        }
                    }
                );
            }
        });
    }

    GetStatusColor() {
        switch(this.Entity.Status) {
            case EnumNotificationStatuses.Sent:
                return 'green-cyan';
            case EnumNotificationStatuses.Failure:
                return 'cinnabar';
            case EnumNotificationStatuses.Sending:
                return 'atomic-tangerine';
            case EnumNotificationStatuses.Draft:
                return 'portage';
        }
    }

    private IsValid() : boolean {
        if(!this.State.Recipients?.length){
            this.UI.StopMessage(this.RC('msgRecipientsAreEmpty'));
            return false;
        }

        return true;
    }

    private InitDeliveryOptions() {
        this.State.NotificationDeliveryOptions = this.LIST('NotificationDispatchDeliveryOptions')
            .filter(x => {
                switch (x.Value) {
                    case EnumNotificationDeliveryOptions.Email:
                        return this.SystemOptionFlag(EnumSystemOptions.IsSupportNotificationMethodEmail);
                    case EnumNotificationDeliveryOptions.SMS:
                        return this.SystemOptionFlag(EnumSystemOptions.IsSupportNotificationMethodSMS);
                    case EnumNotificationDeliveryOptions.WhatsApp:
                        return this.SystemOptionFlag(EnumSystemOptions.IsSupportNotificationMethodWhatsApp);
                    case EnumNotificationDeliveryOptions.PushMessage:
                        return true;
                    default:
                        return false;
                }
            });

        this.Entity.DeliveryMethod = this.State.NotificationDeliveryOptions[0]?.Value;
    }

    private LoadNotificationTemplates() {
        this.DataSources['NotificationTemplates'] = new DataSourceCustomAction((options: DataLoadOptions) => 
            this.notificationTemplateEndpoint.GetList(options));
            
    }

    private NotificationBody() : string {
        if(this.State.IsSimplifiedHtmlBodyEdit) {
            return '';
        }

        switch (this.Entity.DeliveryMethod) {
            case EnumNotificationDeliveryOptions.Email:
                return this.State.NotificationTemplate?.HTMLBody;
            case EnumNotificationDeliveryOptions.PushMessage:
                return this.State.NotificationTemplate?.PushMessageBody;
            case EnumNotificationDeliveryOptions.WhatsApp:
                return this.State.NotificationTemplate?.WhatsAppBody;
            case EnumNotificationDeliveryOptions.SMS:
                return this.State.NotificationTemplate?.SMSBody;
            default:
                return undefined;
        }
    }
}